import * as React from "react";
import BaseDialog, { Field, FieldSet } from "../modelDialogs/BaseDialog";
import { useEffect, useRef, useState } from "react";
import { LocalEtsAppUrl, etsFetch } from "../utils/axios";
import { showToast } from "../components/Toasts";
import Logger from "../Logger";
import { itx } from "../translationItx/itx";
import { closeLocalEtsWebSocket, localEtsWebSocket, openLocalEtsWebSocket } from "../etsFunctions/etsWebSocket";

const logger = new Logger();
const LOCAL_ETS_WS_OPEN_TIMEOUT_MS = 2000;
const LOCAL_ETS_WS_COMMAND_TIMEOUT_MS = 5000;

export default function GroupMonitorDialog(props: {
  className?: string;
  hideXButton?: boolean;
  windowWidth: number;
}): JSX.Element {
  const { className, hideXButton, windowWidth } = props;

  const divRef = useRef<HTMLDivElement>(null);

  const [diagnosticsInProgress, setDiagnosticsInProgress] = useState(false);
  const [groupValues, setGroupValues] = useState([]);
  const [diagnosticGroupAddress, setDiagnosticGroupAddress] = useState("");
  const [diagnosticDPT, setDiagnosticDPT] = useState("1.* 1-bit"); // no itx
  const [diagnosticValue, setDiagnosticValue] = useState("");
  const [diagnosticLastRecievedValue, setDiagnosticLastRecievedValue] = useState("");

  const dataTypes = [
    {
      name: "1.* 1-bit", // no itx
      type: "text",
      values: [{ key: "000", value: "000" }],
    },
    {
      name: "1.001 switch", // no itx
      type: "dropdown",
      values: [
        { key: "Off", value: "00" }, // no itx
        { key: "On", value: "01" }, // no itx
      ],
    },
    {
      name: "1.002 boolean", // no itx
      type: "dropdown",
      values: [
        { key: "False", value: "00" }, // no itx
        { key: "True", value: "01" }, // no itx
      ],
    },
    {
      name: "1.003 enable", // no itx
      type: "dropdown",
      values: [
        { key: "Disable", value: "00" }, // no itx
        { key: "Enable", value: "01" }, // no itx
      ],
    },
    {
      name: "1.004 ramp", // no itx
      type: "dropdown",
      values: [
        { key: "No ramp", value: "00" }, // no itx
        { key: "Ramp", value: "01" }, // no itx
      ],
    },
    {
      name: "1.005 alarm", // no itx
      type: "dropdown",
      values: [
        { key: "No Alarm", value: "00" }, // no itx
        { key: "Alarm", value: "01" }, // no itx
      ],
    },
    {
      name: "1.006 binary value", // no itx
      type: "dropdown",
      values: [
        { key: "Low", value: "00" }, // no itx
        { key: "High", value: "01" }, // no itx
      ],
    },
    {
      name: "1.007 step", // no itx
      type: "dropdown",
      values: [
        { key: "Decrease", value: "00" }, // no itx
        { key: "Increase", value: "01" }, // no itx
      ],
    },
    {
      name: "5.001 percentage (0..100)%", // no itx
      type: "text",
      values: [{ key: "0", value: "0" }],
    },
    {
      name: "9.* 2-byte float value", // no itx
      type: "text",
      values: [{ key: "0", value: "0" }],
    },
    {
      name: "10.001 time of day", // no itx
      type: "dropdown",
      values: [
        { key: "False", value: "00" }, // no itx
        { key: "True", value: "01" }, // no itx
      ],
    },
  ];

  const [selectedDataType, setSelectedDataType] = useState(dataTypes[0]);
  const [selectedValue, setSelectedValue] = useState(dataTypes[0].values[0].value);

  //   useEffect(() => {
  //     // Scroll to the bottom of the table when data changes
  //     if (divRef.current) {
  //       divRef.current.scrollTop = divRef.current.scrollHeight;
  //     }
  //   }, [groupValues]);

  useEffect(() => {
    var diagnosticId: any;
    if (diagnosticsInProgress) {
      diagnosticId = setInterval(() => {
        if (diagnosticsInProgress) communicationReadAllTelegrams();
        else clearInterval(diagnosticId);
      }, 5000);
    }
    return () => {
      clearInterval(diagnosticId);
    };
  }, [diagnosticsInProgress]);

  useEffect(() => {
    return () => {
      if (diagnosticsInProgress) {
        if (!state.settings.etsIsOnline) closeDialog();
        communicationStop(true);
      }
      console.log("GroupMonitor dialog unmounted.");
    };
  }, []);

  function closeDialog() {
    state.dialogManager.closeDialog();
    state.dialogManager.openDialog(
      <BaseDialog
        className="dialog-standard"
        title={state.translate("menuBar.etsOffline", "ETS: Offline")}
        onOk={() => {
          return true;
        }}
        okCaption={state.translate("ok", "Ok")}
        noAbortButton={true}
      >
        <div
          style={{
            padding: "20px",
            maxWidth: "600px",
            textAlign: "center",
          }}
        >
          {state.translate(
            "groupMonitor.unexpectedDiagnosticError",
            "Diagnostic mode halted unexpectedly. Please check your ETS connection and try again.",
          )}
        </div>
      </BaseDialog>,
    );
  }

  const handleDataTypeChange = (event: any) => {
    const selectedName = event.target.value;
    setDiagnosticDPT(selectedName);
    setSelectedDataType((dataTypes as any).find((dataType: any) => dataType.name === selectedName));
    setSelectedValue("");
  };

  const handleValueChange = (event: any) => {
    setDiagnosticValue(event.target.value);
    setSelectedValue(event.target.value);
  };

  async function communicationCommandViaLocalWs(
    action: "start" | "stop" | "clear" | "readall" | "read" | "write",
    body: any = {},
    timeout = LOCAL_ETS_WS_COMMAND_TIMEOUT_MS,
  ): Promise<any | undefined> {
    try {
      openLocalEtsWebSocket();
      await localEtsWebSocket!.waitForOpen(LOCAL_ETS_WS_OPEN_TIMEOUT_MS);
      return await localEtsWebSocket!.sendCommandAsync(`communication/${action}`, body, timeout);
    } catch (ex) {
      logger.error(ex);
      if (localEtsWebSocket?.isClosed) closeLocalEtsWebSocket();
      return undefined;
    }
  }

  async function communicationCommandViaHttpFallback(
    action: "start" | "stop" | "clear" | "readall" | "read" | "write",
    body: any = {},
  ): Promise<any | undefined> {
    const response = await etsFetch(`${LocalEtsAppUrl}/communication/${action}`, {
      method: "POST", // no itx
      body: JSON.stringify(body),
    });
    if (!response?.ok) return undefined;
    return await response.json();
  }

  async function communicationCommand(
    action: "start" | "stop" | "clear" | "readall" | "read" | "write",
    body: any = {},
    timeout = LOCAL_ETS_WS_COMMAND_TIMEOUT_MS,
  ): Promise<any | undefined> {
    const wsResponse = await communicationCommandViaLocalWs(action, body, timeout);
    if (wsResponse !== undefined) return wsResponse;
    return await communicationCommandViaHttpFallback(action, body);
  }

  async function communicationStart(): Promise<boolean> {
    try {
      const responseData = await communicationCommand("start");
      if (!responseData) return false;
      setDiagnosticsInProgress(true);
      showToast({
        text: state.translate("groupMonitor.diagnosticsStarted", "Group Monitor in Progress"),
      });
      return true;
    } catch (ex) {
      return false;
    }
  }

  async function communicationStop(modalClose: boolean): Promise<boolean> {
    try {
      const responseData = await communicationCommand("stop");
      if (!responseData) return false;
      // if (response?.status == "OK") {
      setDiagnosticsInProgress(false);
      if (!modalClose) {
        showToast({
          text: state.translate("groupMonitor.diagnosticsStopped", "Group Monitor Stopped"),
        });
      }

      return true;
      // } else {
      //   return false;
      // }
    } catch (ex) {
      return false;
    }
  }

  async function communicationClear(): Promise<boolean> {
    try {
      const responseData = await communicationCommand("clear");
      if (!responseData) return false;
      await communicationReadAllTelegrams();
      // if (response?.status == "OK") {
      // setDiagnosticsInProgress(false);
      showToast({
        text: state.translate("groupMonitor.telegramsCleared", "Telegrams Cleared"),
      });
      return true;
      // } else {
      //   return false;
      // }
    } catch (ex) {
      return false;
    }
  }

  async function communicationReadAllTelegrams(): Promise<any> {
    if (!state.settings.etsIsOnline) {
      closeDialog();
      return false;
    }
    try {
      const responseData = await communicationCommand("readall");
      if (responseData?.status == "OK") {
        if (responseData?.data) {
          setGroupValues(responseData.data);
        }
        return true;
      }
      return false;
    } catch (ex) {
      return false;
    }
  }

  async function communicationReadAddressValue(): Promise<any> {
    try {
      const responseData = await communicationCommand("read", { address: diagnosticGroupAddress });
      if (responseData?.status == "OK" && responseData?.body) {
        setDiagnosticLastRecievedValue(`${diagnosticGroupAddress} == ${responseData.body.groupValue}`);
        showToast({
          text: state
            .translate("menuBar.success", "Current Value at Group Address $ : #")
            .replaceAll("$", diagnosticGroupAddress)
            .replaceAll("#", responseData.body.groupValue),
        });
      }
      return true;
    } catch (ex) {
      return false;
    }
  }

  async function communicationWriteAddressValue(): Promise<any> {
    try {
      const responseData = await communicationCommand("write", {
        address: diagnosticGroupAddress,
        dpt: diagnosticDPT,
        value: diagnosticValue,
      });
      if (responseData?.status == "OK") {
        showToast({
          text: state.translate(
            "groupMonitor.valueWritten",
            "Value successfully written to Destination Group Address",
          ),
        });
        return true;
      }
      return false;
    } catch (ex) {
      return false;
    }
  }

  function isValidFormFields() {
    const isValid =
      /^\d+\/\d+\/\d+$/.test(diagnosticGroupAddress) && /^-?\d+$/.test(diagnosticValue) && diagnosticDPT == "switch";
    return isValid;
  }

  //   showToast({
  //     text: state.translate(
  //       "groupMonitor.telegramsRead",
  //       "All Telegrams retrieved from server.",
  //     ),
  //   });

  return (
    <BaseDialog
      title={state.translate("groupMonitor.groupMonitor", `Group Monitor`)}
      noOkButton={true}
      abortCaption={itx("menuBar.close", "Close")}
      onClose={() => {
        return true;
      }}
    >
      <span>
        <button
          className="button"
          disabled={diagnosticsInProgress}
          onClick={async () => {
            if (!state.settings.etsIsOnline) closeDialog();
            await communicationStart();
          }}
        >
          {state.translate("groupMonitor.start", "Start")}
        </button>
        <button
          className="button"
          disabled={!diagnosticsInProgress}
          onClick={async () => {
            if (!state.settings.etsIsOnline) closeDialog();
            await communicationStop(false);
          }}
        >
          {state.translate("groupMonitor.stop", "Stop")}
        </button>
        <button
          className="button"
          disabled={groupValues.length == 0}
          onClick={async () => {
            if (!state.settings.etsIsOnline) closeDialog();
            await communicationClear();
          }}
        >
          {state.translate("groupMonitor.clear", "Clear")}
        </button>
        <button
          className="button"
          onClick={async () => {
            await communicationReadAllTelegrams();
          }}
        >
          {state.translate("groupMonitor.refresh", "Refresh")}
        </button>
      </span>
      <span style={{ float: "right" }}>
        <button
          className="button"
          disabled={!/^\d+\/\d+\/\d+$/.test(diagnosticGroupAddress) && !diagnosticsInProgress}
          onClick={async () => {
            if (!state.settings.etsIsOnline) closeDialog();
            await communicationReadAddressValue();
          }}
        >
          {state.translate("groupMonitor.read", "Read")}
        </button>
        <button
          className="button"
          disabled={!isValidFormFields() && !diagnosticsInProgress}
          onClick={async () => {
            if (!state.settings.etsIsOnline) closeDialog();
            await communicationWriteAddressValue();
          }}
        >
          {state.translate("groupMonitor.write", "Write")}
        </button>
      </span>

      <div
        style={{
          padding: "0 0 20px 0",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div
          id="leftColumn"
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            width: "50%",
            paddingRight: "20px",
          }}
        >
          <FieldSet>
            <Field label={state.translate("groupMonitor.groupAddress", "Group Address")}>
              <input
                key={"grpAddress"} // no itx
                className="input"
                maxLength={state.maxCharLimit.text}
                defaultValue={diagnosticGroupAddress}
                placeholder={itx("groupMonitor.placeholder.groupAddress", "e.g. 1/1/68")}
                onBlur={(ev) => {
                  setDiagnosticGroupAddress(ev.target.value);
                }}
              ></input>
            </Field>

            <Field label={state.translate("groupMonitor.lastRecievedValue", "Last Read")}>
              <input
                key={"lastRead"} // no itx
                className="input"
                disabled={true}
                maxLength={state.maxCharLimit.text}
                value={diagnosticLastRecievedValue}
              ></input>
            </Field>
          </FieldSet>
        </div>
        <div
          id="rightColumn"
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            width: "50%",
            paddingRight: "20px",
          }}
        >
          <FieldSet>
            <Field label={state.translate("groupMonitor.dpt", "DPT")}>
              <select
                key={"dpt"} // no itx
                className="select"
                defaultValue={diagnosticDPT}
                onChange={handleDataTypeChange}
                // onBlur={(ev) => {
                //   setDiagnosticDPT(ev.target.value);
                // }}
              >
                {dataTypes.map((dataType) => (
                  <option key={dataType.name} value={dataType.name}>
                    {dataType.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={state.translate("groupMonitor.value", "Value")}>
              {selectedDataType.values.length === 1 ? (
                <input
                  key={"diagnosticvalue"} // no itx
                  className="input"
                  maxLength={state.maxCharLimit.smallText}
                  defaultValue={selectedValue}
                  onChange={handleValueChange}
                  // onBlur={(ev) => {
                  //   setDiagnosticValue(ev.target.value);
                  // }}
                />
              ) : (
                <select
                  key={"diagnosticvalue"} // no itx
                  className="select"
                  defaultValue={selectedValue}
                  onChange={handleValueChange}
                >
                  {selectedDataType.values.map((value: any) => (
                    <option key={value.key} value={value.value}>
                      {value.key}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </FieldSet>
        </div>
      </div>

      <div
        ref={divRef}
        style={{
          height: "300px",
          maxHeight: "300px",
          overflow: "auto",
          border: "0.5px solid gray",
        }}
      >
        <table className="diagnosticTable" id="diagnosticTable" style={{ width: `${windowWidth}px` }}>
          <thead>
            <tr
              style={{
                color: "var(--knxColorWhite)",
                backgroundColor: "var(--knxColorGrey)",
              }}
            >
              {/* <th style={{ width: "25%", paddingLeft: "10px" }}>
                {state.translate("groupMonitor.timestamp", "Timestamp")}
              </th> */}
              <th style={{ width: "5%", paddingLeft: "20px" }}> #</th>
              <th style={{ width: "10%" }}>{state.translate("groupMonitor.priority", "Priority")}</th>
              <th style={{ width: "15%" }}>{state.translate("groupMonitor.sourceAddress", "Source Address")}</th>
              <th style={{ width: "20%" }}>
                {state.translate("groupMonitor.destinationAddress", "Destination Address")}
              </th>
              <th style={{ width: "10%" }}>{state.translate("groupMonitor.value", "Value")}</th>
              <th style={{ width: "10%" }}>{state.translate("groupMonitor.less7Bits", "Less7Bits")}</th>
              <th style={{ width: "10%" }}>{state.translate("groupMonitor.hopCount", "HopCount")}</th>
              <th style={{ width: "20%" }}>{state.translate("groupMonitor.type", "Type")}</th>
              {/* <th style={{ width: "10%" }}>
                {state.translate("groupMonitor.hashCode", "HashCode")}
              </th> */}
              {/* <th style={{ width: "10%" }}>
                  {state.translate("groupMonitor.service", "Service")}
                </th> */}
            </tr>
          </thead>
          <tbody>
            {groupValues.length == 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center" }}>
                  {state.translate("groupMonitor.noEntriesTodisplay", " ------ No entries to display. ------")}
                </td>
              </tr>
            )}
            {groupValues.length > 0 &&
              groupValues.map((item: any, index: number) => (
                <tr key={index}>
                  <td style={{ fontWeight: "bold" }}>{index + 1}</td>
                  <td>{item.Priority}</td>
                  <td>{item.SourceAddress}</td>
                  <td>{item.DestinationAddress}</td>
                  <td>{item.Value}</td>
                  <td>{item.Less7Bits}</td>
                  <td>{item.HopCount}</td>
                  <td>{`Group_${item.Service}`}</td>
                  {/* <td>{item.HashCode}</td> */}
                  {/* <td>{item.Type}</td> */}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </BaseDialog>
  );
}
